import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GreetingType, MusicOnHoldType } from '../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { Observable, of, Subscription } from 'rxjs';
import {
  ZiroFormConfig,
  ZiroFormsMessage,
  ZiroFormsUpdate,
  ZiroFormsValidationState,
} from '../../../../forms/ziro-forms-models';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { ZiroFileUploadConfig } from '../../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroTextareaConfig } from '../../../../forms/fields/textarea/ziro-textarea.component';
import { UiAudioSettings } from '../teams-call-queue-edit-form.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

@Component({
  selector: 'ziro-audio-form',
  imports: [ZiroFormsModule, TranslateModule],
  templateUrl: './audio-form.component.html',
})
export class AudioFormComponent extends ZiroFormAbstractDirective<UiAudioSettings> implements OnInit, OnDestroy {
  private _translateService = inject(TranslateService);

  isGreetingLoading = signal(true);
  greetingUploadURL = signal('');
  greetingDownloadName = signal('');
  greetingFileExtension = signal('');

  isMusicOnHoldLoading = signal(true);
  musicOnHoldUploadURL = signal('');
  musicOnHoldDownloadName = signal('');
  musicOnHoldFileExtension = signal('');

  acceptedFileTypes = ['.wav', '.wma', '.mp3'];

  private _isInitialLoad = true;
  private _initialGreetingName: string;
  private _initialMusicOnHoldName: string;
  private _subs = new Subscription();

  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    this._initialGreetingName = this.entity?.greetingUpload?.name;
    this._initialMusicOnHoldName = this.entity?.musicOnHoldUpload?.name;

    const updateSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<UiAudioSettings>) => {
      this._updateAudioSettingState(update);
      this._isInitialLoad = false;
    });

    this._subs.add(updateSub);

    this.formConfig = {
      fields: {
        greetingType: {
          dataAutomation: 'greeting-type',
          label: 'tkey;teams_call_queue.audio_settings.greeting_header',
          componentConfig: new ZiroRadioConfig<GreetingType>({
            stackLabelAndOptions: true,
            buttons: [
              {
                value: GreetingType.NONE,
                label: 'tkey;teams_call_queue.audio_settings.greeting.no_greeting',
              },
              {
                value: GreetingType.CUSTOM_GREETING,
                label: 'tkey;teams_call_queue.audio_settings.greeting.custom',
              },
              {
                value: GreetingType.TTS_MESSAGE,
                label: 'tkey;teams_call_queue.audio_settings.greeting.text_to_speech',
              },
            ],
          }),
        },
        greetingUpload: {
          dataAutomation: 'greeting-upload',
          label: '',
          required: () => this.formData.greetingType === GreetingType.CUSTOM_GREETING,
          componentConfig: new ZiroFileUploadConfig({
            acceptedFileExtensions: { acceptedExtensions: this.acceptedFileTypes.join(', '), allowOthers: false },
            isAudioFile: true,
          }),
          validation: [
            {
              validator: (val: File) => {
                return !!val && val.name.length > 64
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant('tkey;global.validation.file_name.max_length', {
                maxLength: 64,
              }),
            },
            {
              validator: (val: File): ZiroFormsValidationState => {
                if (val?.name !== this._initialGreetingName) {
                  return val?.size <= 5000000 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                }
              },
              message: (): ZiroFormsMessage => {
                return {
                  content: 'tkey;shared.fileselect.validators.max_size',
                  params: {
                    size: '5 MB',
                  },
                };
              },
            },
          ],
        },
        greetingTtsMessage: {
          dataAutomation: 'greeting-tts',
          label: '',
          required: () => this.formData.greetingType === GreetingType.TTS_MESSAGE,
          hidden: () => this.formData.greetingType !== GreetingType.TTS_MESSAGE,
          componentConfig: new ZiroTextareaConfig({
            placeholder: this._translateService.instant(
              'tkey;teams_call_queue.audio_settings.greeting.text_to_speech.placeholder',
            ),
          }),
          validation: [
            {
              validator: (val: string, greetingType: GreetingType) => {
                if (greetingType !== GreetingType.TTS_MESSAGE) {
                  return ZiroFormsValidationState.VALID;
                } else {
                  return val && val.length > 10 && val.length < 1000
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                }
              },
              injectValuesFromFields: ['greetingType'],
              message: () =>
                this._translateService.instant('tkey;validators.global.min_max.char.error', {
                  min: 10,
                  max: 1000,
                }),
            },
          ],
        },
        musicOnHoldType: {
          dataAutomation: 'moh-type',
          label: 'tkey;teams_call_queue.audio_settings.moh_header',
          componentConfig: new ZiroRadioConfig<MusicOnHoldType>({
            stackLabelAndOptions: true,
            buttons: [
              {
                value: MusicOnHoldType.NONE,
                label: 'tkey;teams_call_queue.audio_settings.moh.no_greeting',
              },
              {
                value: MusicOnHoldType.CUSTOM_MUSIC,
                label: 'tkey;teams_call_queue.audio_settings.moh.custom',
              },
            ],
          }),
        },
        musicOnHoldUpload: {
          dataAutomation: 'moh-upload',
          label: '',
          required: () => this.formData.musicOnHoldType === MusicOnHoldType.CUSTOM_MUSIC,
          componentConfig: new ZiroFileUploadConfig({
            acceptedFileExtensions: { acceptedExtensions: this.acceptedFileTypes.join(', '), allowOthers: false },
            isAudioFile: true,
          }),
          validation: [
            {
              validator: (val: File) => {
                return !!val && val.name.length > 64
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant('tkey;global.validation.file_name.max_length', {
                maxLength: 64,
              }),
            },
            {
              validator: (val: File): ZiroFormsValidationState => {
                if (val?.name !== this._initialMusicOnHoldName) {
                  return val?.size <= 5000000 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                }
              },
              message: (): ZiroFormsMessage => {
                return {
                  content: 'tkey;shared.fileselect.validators.max_size',
                  params: {
                    size: '5 MB',
                  },
                };
              },
            },
          ],
        },
      },
      options: {
        columnClasses: {
          label: 'text-start',
          input: '',
        },
      },
    };
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  protected submit(): Observable<void> {
    return of(null);
  }

  triggerSubmitValidation(): void {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
  }

  protected readonly GreetingType = GreetingType;
  protected readonly MusicOnHoldType = MusicOnHoldType;
  ziroIcons = ZiroIcons;

  private _updateAudioSettingState(update: ZiroFormsUpdate<UiAudioSettings>): void {
    const { greetingUpload: newGreetingUpload } = update.new;
    const { greetingUpload: oldGreetingUpload } = update.old;
    const { musicOnHoldUpload: newMusicOnHoldUpload } = update.new;
    const { musicOnHoldUpload: oldMusicOnHoldUpload } = update.old;

    if (
      (newGreetingUpload && newGreetingUpload.name !== oldGreetingUpload?.name) ||
      (newGreetingUpload && this._isInitialLoad)
    ) {
      const lastDot = newGreetingUpload.name.lastIndexOf('.');
      this.greetingFileExtension.update(() => newGreetingUpload.name.substring(lastDot));
      this.isGreetingLoading.update(() => true);
      this.greetingUploadURL.update(() => URL.createObjectURL(newGreetingUpload));
      this.greetingDownloadName.update(() => newGreetingUpload.name);
      this.isGreetingLoading.update(() => false);
    }

    if (
      (newMusicOnHoldUpload && newMusicOnHoldUpload.name !== oldMusicOnHoldUpload?.name) ||
      (newMusicOnHoldUpload && this._isInitialLoad)
    ) {
      const lastDot = newMusicOnHoldUpload.name.lastIndexOf('.');
      this.musicOnHoldFileExtension.update(() => newMusicOnHoldUpload.name.substring(lastDot));
      this.isMusicOnHoldLoading.update(() => true);
      this.musicOnHoldUploadURL.update(() => URL.createObjectURL(newMusicOnHoldUpload));
      this.musicOnHoldDownloadName.update(() => newMusicOnHoldUpload.name);
      this.isMusicOnHoldLoading.update(() => false);
    }
  }
}
